import {
  ReactStateDeclaration,
  TargetState,
  useCurrentStateAndParams,
  useRouter,
} from "@uirouter/react";
import { find, intersection, omit } from "lodash";
import React, { ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { router } from "../../router";
import { AppStore } from "../../store";
import { AuthDTO } from "../../store/auth/models/dto";
import { Roles } from "../../store/auth/models/internal";
import { Fetchable } from "../../store/common/fetchable/helpers";
import { AndOrTreeNode } from "../../utils/and-or-tree";
import { IconType } from "../icons";
import { LightItem } from "../menu/item/light";
import { ItemProps } from "./item/base";
import { PrimaryItem } from "./item/primary";
import { WhiteItem } from "./item/white";

export type MenuRoute = Omit<ReactStateDeclaration, "data"> & {
  data: {
    title: string;
    disabled?: boolean;
    roles?: AndOrTreeNode;
    [s: string]: any;
  };
};

export namespace MenuNode {
  type GenericNode = {
    icon: IconType;
    disabled?: boolean;
    roles?: AndOrTreeNode;
    adornment?: ReactNode;
    renderTitle?: ({ title }: { title: string }) => JSX.Element;
  };

  type TargetLeafNode = GenericNode & {
    target: TargetState;
    title: string;
  };

  export type LeafNode =
    | (GenericNode & {
        route: MenuRoute;
      })
    | TargetLeafNode;

  export type ParentNode = GenericNode & {
    children: Array<MenuNode>;
    title: string;
  };

  export type MenuNode = LeafNode | ParentNode;

  export type AugmentedLeafNode = MenuNode.LeafNode & { selected: boolean };

  export type AugmentedParentNode = Omit<MenuNode.ParentNode, "children"> & {
    children: Array<AugmentedNode>;
    selected: boolean;
  };

  export type AugmentedNode = AugmentedLeafNode | AugmentedParentNode;

  export function isLeafNode(m: MenuNode): m is LeafNode {
    return (m as any).route !== undefined || (m as any).target !== undefined;
  }

  export function isLeafTargeted(l: LeafNode): l is TargetLeafNode {
    return (l as any).target !== undefined;
  }

  export function isAugmentedLeafNode(
    m: AugmentedNode
  ): m is AugmentedLeafNode {
    return (m as any).route !== undefined || (m as any).target !== undefined;
  }

  export function isParentNode(m: MenuNode): m is ParentNode {
    return (m as any).children !== undefined;
  }
}

export const Menu = styled.ul`
  list-style: none;
  padding: 0;
  width: 100%;
`;

const ChildrenWrapper = styled.div<{ expanded?: boolean }>`
  transition: max-height 0.3s ease-in-out;
  max-height: ${(props) => (props.expanded ? "1000" : "0")}px;
  overflow: hidden;
`;

export interface MenuItemProps {
  node: MenuNode.AugmentedNode;
  level?: ItemProps["tab"];
  routeOnOpen?: boolean;
}

function getFirstAvailableRoute(
  node: MenuNode.AugmentedParentNode
): TargetState | undefined {
  for (let i = 0; i < node.children.length; i++) {
    const child = node.children[i];
    if (MenuNode.isAugmentedLeafNode(child)) {
      return MenuNode.isLeafTargeted(child)
        ? child.target
        : router.stateService.target(child.route);
    }
    const descendant = getFirstAvailableRoute(child);
    if (descendant) {
      return descendant;
    }
  }
}

export const MenuItem: React.FunctionComponent<ItemProps & MenuItemProps> = (
  props
) => {
  const { node, level = 0, routeOnOpen } = props;
  const [expanded, setExpanded] = useState(node.selected);
  const router = useRouter();
  useCurrentStateAndParams();
  const { t } = useTranslation("page-titles");

  const authFetchable = useSelector<
    AppStore,
    Fetchable<AuthDTO.Responses.Login.T>
  >((state) => state.auth.login);

  const userRoles = useMemo(
    () =>
      authFetchable.status === "ready"
        ? authFetchable.data.accessToken.roles
        : undefined,
    [authFetchable]
  );

  const routeTitle = useMemo(
    () =>
      MenuNode.isAugmentedLeafNode(node)
        ? MenuNode.isLeafTargeted(node)
          ? t(node.title)
          : t(node.route.data.title)
        : t(node.title),
    [node, t]
  );

  const propsToPass = omit(
    props,
    "routeOnOpen",
    "node",
    "level",
    "selected",
    "children",
    "icon"
  );

  const filteredChildren = useMemo(
    () =>
      MenuNode.isAugmentedLeafNode(node)
        ? undefined
        : node.children?.filter(
            (x) =>
              x.roles === undefined || (userRoles && x.roles.exec(userRoles))
          ),
    [node]
  );

  if (MenuNode.isAugmentedLeafNode(node)) {
    const { icon, disabled, adornment, renderTitle } = node;

    const isActive = router.stateService.is(
      MenuNode.isLeafTargeted(node) ? node.target.state() : node.route
    );

    const onClickHandler = () =>
      MenuNode.isLeafTargeted(node)
        ? router.stateService.go(
            node.target.state(),
            node.target.params(),
            node.target.options()
          )
        : router.stateService.go(node.route);

    if (isActive) {
      return (
        <PrimaryItem
          icon={icon}
          tab={level}
          chevronState={expanded ? "expanded" : "collapsed"}
          onClick={onClickHandler}
          variant="main"
          {...propsToPass}
        >
          {renderTitle !== undefined
            ? React.createElement(renderTitle, {
                title: routeTitle,
              })
            : routeTitle}{" "}
          {adornment}
        </PrimaryItem>
      );
    }
    if (node.selected) {
      return (
        <LightItem
          icon={icon}
          tab={level}
          variant="leaf"
          onClick={onClickHandler}
          chevronState="collapsed"
          {...propsToPass}
        >
          {renderTitle !== undefined
            ? React.createElement(renderTitle, {
                title: routeTitle,
              })
            : routeTitle}{" "}
          {adornment}
        </LightItem>
      );
    }

    return (
      <WhiteItem
        icon={icon}
        tab={level}
        onClick={disabled ? undefined : onClickHandler}
        chevronState="collapsed"
        disabled={disabled}
        {...propsToPass}
      >
        {renderTitle !== undefined
          ? React.createElement(renderTitle, {
              title: routeTitle,
            })
          : routeTitle}{" "}
        {adornment}
      </WhiteItem>
    );
  } else {
    const { icon, title, selected, disabled, adornment, renderTitle } = node;
    const onClickHandler = () => {
      if (!expanded && routeOnOpen) {
        const target = getFirstAvailableRoute(node);
        if (target)
          router.stateService.go(
            target.name(),
            target.params(),
            target.options()
          );
      }
      setExpanded(!expanded);
    };

    if (expanded || selected) {
      return (
        <>
          <LightItem
            icon={icon}
            tab={level}
            variant={expanded ? "main" : "leaf"}
            chevron
            chevronState={expanded ? "expanded" : "collapsed"}
            onClick={!selected ? onClickHandler : undefined}
            {...propsToPass}
          >
            {renderTitle !== undefined
              ? React.createElement(renderTitle, {
                  title: t(title),
                })
              : t(title)}{" "}
            {adornment}
          </LightItem>
          <ChildrenWrapper expanded>
            {filteredChildren?.map((node) => {
              const title = MenuNode.isAugmentedLeafNode(node)
                ? MenuNode.isLeafTargeted(node)
                  ? t(node.title)
                  : t(node.route.data.title)
                : node.title;
              return (
                <MenuItem
                  key={title}
                  node={node}
                  level={level + 1}
                  {...propsToPass}
                />
              );
            })}
          </ChildrenWrapper>
        </>
      );
    }

    return (
      <>
        <WhiteItem
          icon={icon}
          tab={level}
          chevron
          onClick={disabled ? undefined : onClickHandler}
          disabled={disabled}
          chevronState="collapsed"
          {...propsToPass}
        >
          {renderTitle !== undefined
            ? React.createElement(renderTitle, {
                title: t(title),
              })
            : t(title)}{" "}
          {adornment}
        </WhiteItem>
        <ChildrenWrapper>
          {filteredChildren?.map((node) => {
            const title = MenuNode.isAugmentedLeafNode(node)
              ? MenuNode.isLeafTargeted(node)
                ? t(node.title)
                : t(node.route.data.title)
              : node.title;
            return (
              <MenuItem
                key={title}
                node={node}
                level={level + 1}
                {...propsToPass}
              />
            );
          })}
        </ChildrenWrapper>
      </>
    );
  }
};
